// P-AUTH-GATE-AND-ONBOARDING-DRAFT (closes tracker #40, QA-AUDIT-2026-06-01
// Area 1 P1: "Onboarding draft does not survive refresh").
//
// PURE storage core for the onboarding draft — no localStorage I/O, no React.
// onboardingDraft.ts owns the impure read/write wrappers (try/catch + Sentry);
// this module owns the SERIALIZE / DESERIALIZE / VERSION / TTL / KEY logic so
// every branch is unit-testable without a DOM.
//
// ─────────────────────────────────────────────────────────────────────────
// FORWARD-COMPATIBILITY CONTRACT (the load-bearing design decision)
// ─────────────────────────────────────────────────────────────────────────
// The persisted envelope is VERSIONED so PR-F (the onboarding redesign per
// onboarding-science-deep-research.md) can extend it WITHOUT a migration:
//
//   (a) ADDITIVE change (new optional field) → stays `version: 1`. A v1 draft
//       written before the field existed simply lacks it; `fromPersisted`
//       default-fills every field it reads, so old drafts load fine.
//   (b) BREAKING change (an existing field's meaning/shape changes) → PR-F
//       bumps `version` to 2. `fromPersisted` returns null for any non-1
//       envelope, so v1 drafts are DISCARDED on read (blown away), never
//       migrated. This matches the brief's "don't migrate differently-shaped
//       data — blow it away on first run."
//
// The research-derived target shape PR-F will populate is reserved here as
// `PriorResponse` (per-question {answer, confidence}, audience + domain
// namespacing, tried_in_app). The CURRENT flow does NOT populate `priors`:
// today's confidence-aware answers already live in the flat `parentPriors`
// map (`<slug>: bool` + `<slug>_confidence` siblings, domain-grouped via
// ABILITY_OPTIONS — see lib/engine/parentPriors). `priors` is the structured
// form PR-F migrates that map into, additively. It is typed-but-unpopulated
// on purpose — a reserved extension point, NOT a shipped feature.

import type { ParentPriors } from "@/App/progress";
import type { OnboardingDraft } from "./onboardingDraft";

/** Current persisted-envelope version. Bump ONLY for a breaking shape change
 *  (see the forward-compatibility contract above); additive fields stay at 1. */
export const DRAFT_SCHEMA_VERSION = 1 as const;

/** Draft lifetime. A half-entered onboarding older than this is treated as
 *  abandoned and discarded on read (the parent starts fresh). 24h per the
 *  brief — long enough to survive an accidental refresh / closed tab within a
 *  sitting or overnight, short enough that stale half-entered PII does not
 *  linger. Calibration target (not a cited constant): tune against onboarding
 *  resume-rate if instrumented. */
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

/** localStorage key, scoped PER auth user so a shared device where account A
 *  then account B sign in never cross-reads a half-entered draft. */
export function draftStorageKey(userId: string): string {
  return `pencilwise:onboardingDraft:${userId}`;
}

// ── Forward-compat vocabulary (RESERVED for PR-F — see contract above) ──────
/** Confidence scale from onboarding-science-deep-research.md (§"behavior-
 *  anchored, confidence-aware"): Usually on their own / Sometimes with help /
 *  Not yet / Not sure. PR-F maps the live flow's AbilityResponse onto these. */
type PriorConfidence = "alone" | "with_help" | "not_yet" | "not_sure";
/** Who answered a given question. The research separates parent-judgment
 *  items from child-performed items (and parent-with-kid co-use moments). */
type PriorAudience = "parent" | "kid" | "parent_with_kid";
/** Domain namespacing — the research separates reading and math because
 *  parents calibrate asymmetrically across strands; foundation is the
 *  pre-reading/pre-math strand. */
type PriorDomain = "reading" | "math" | "foundation";

/** RESERVED — the structured per-question response shape PR-F will populate.
 *  Not produced by the current flow (see contract above). */
interface PriorResponse {
  questionId: string;
  domain: PriorDomain;
  audience: PriorAudience;
  answer: string | number | boolean | null;
  confidence: PriorConfidence | null;
  triedInApp: boolean;
}

/** The on-disk envelope. Field names follow the brief's forward-compat sketch
 *  (childName / palChoice) so PR-F's reader speaks the same schema. */
interface PersistedOnboardingDraftV1 {
  version: typeof DRAFT_SCHEMA_VERSION;
  /** epoch ms at write time — the TTL anchor. */
  savedAt: number;
  childName: string;
  age: number | null;
  locale: string;
  /** the chosen pencil-pal mascot id (live draft.mascotId). */
  palChoice: string | null;
  /** live flow's flat ability map — faithfully roundtripped (the handoff
   *  bridge reads draft.parentPriors verbatim). Source of truth today. */
  parentPriors: ParentPriors;
  /** RESERVED for PR-F (see contract). Unpopulated by the current flow. */
  priors?: PriorResponse[];
}

/** Live draft → persisted envelope. Pure; `now` injected for tests. */
export function toPersisted(
  draft: OnboardingDraft,
  now: number,
): PersistedOnboardingDraftV1 {
  return {
    version: DRAFT_SCHEMA_VERSION,
    savedAt: now,
    childName: draft.name ?? "",
    age: draft.age ?? null,
    locale: draft.locale ?? "en-US",
    palChoice: draft.mascotId ?? null,
    parentPriors: draft.parentPriors ?? {},
    // `priors` intentionally omitted — reserved for PR-F (forward-compat
    // contract). A tolerant reader treats its absence as [].
  };
}

/**
 * Persisted envelope (already JSON-parsed) → live draft, or null when the
 * record must be DISCARDED (wrong/absent version, expired by TTL, or
 * unparseable shape). Pure; `now` + `ttlMs` injected for tests.
 *
 * Every field is default-filled so a partial / future-additive record still
 * yields a usable draft (forward-compat clause (a)).
 */
export function fromPersisted(
  parsed: unknown,
  now: number,
  ttlMs: number = DRAFT_TTL_MS,
): OnboardingDraft | null {
  if (!parsed || typeof parsed !== "object") return null;
  const env = parsed as Record<string, unknown>;

  // Version gate — anything but the current version is blown away, not
  // migrated (forward-compat clause (b)).
  if (env.version !== DRAFT_SCHEMA_VERSION) return null;

  // TTL gate — a non-finite or expired savedAt discards the draft. A future
  // savedAt (device clock behind) yields a negative age < ttl → kept (we
  // don't punish clock skew by wiping a fresh draft).
  const savedAt = typeof env.savedAt === "number" ? env.savedAt : NaN;
  if (!Number.isFinite(savedAt)) return null;
  if (now - savedAt > ttlMs) return null;

  return {
    name: typeof env.childName === "string" ? env.childName : "",
    age: typeof env.age === "number" ? env.age : null,
    locale: typeof env.locale === "string" ? env.locale : "en-US",
    mascotId: typeof env.palChoice === "string" ? env.palChoice : null,
    parentPriors:
      env.parentPriors && typeof env.parentPriors === "object"
        ? (env.parentPriors as ParentPriors)
        : {},
  };
}
